/**
 * Score tiles (DESIGN.md "Cards / Containers"), 24px padding on layer-01.
 *   pillar  The pillar page's score: a swatch-led title, the 6rem light
 *           numeral over "/ 100" (or a `suffix` such as "%"), then a meta row
 *           (the delta tag, a helper note such as "Weighted by severity") and
 *           an optional `visual` (the 30-point grid). No verdict (D22).
 *   hero    The Command Center's Guardian Score: the same title and numeral,
 *           the six-word verdict at 1.25rem/400 and the delta tag on the
 *           left; `aside` (the waffle and its key) 32px to the right. The
 *           columns stack on phones.
 * `as` renders the tile as a `section` or `aside` landmark, named by its title (`headingId`).
 */
import clsx from 'clsx'
import { BigNumber } from './BigNumber'
import { Tile } from './Tile'
import { TileHeader } from './TileHeader'
import type { ScoreTileProps } from './types'

export function ScoreTile({
  title,
  category,
  score,
  of = 100,
  suffix,
  delta,
  meta,
  verdict,
  aside,
  visual,
  size = 'pillar',
  as,
  level = 2,
  headingId,
  isLoading = false,
  className,
}: ScoreTileProps) {
  // A landmark element is named by the title.
  const landmark = as && as !== 'div' && headingId ? { as, 'aria-labelledby': headingId } : { as }
  const header = <TileHeader title={title} category={category} level={level} id={headingId} />
  const number = <BigNumber value={isLoading ? null : score} of={of} suffix={suffix} size="score" className="mt-4" />
  const metaRow = (spacing?: string) =>
    delta || meta ? (
      <div className={clsx('flex flex-wrap items-center gap-x-3 gap-y-2', spacing)}>
        {delta}
        {meta ? <span className="text-helper text-text-helper">{meta}</span> : null}
      </div>
    ) : null

  if (size === 'hero') {
    const showVerdict = !!verdict && !isLoading
    return (
      <Tile
        {...landmark}
        padding={24}
        aria-busy={isLoading || undefined}
        className={clsx('grid grid-cols-1 gap-6 c-md:grid-cols-[minmax(0,1fr)_auto] c-md:gap-8', className)}
      >
        <div className="min-w-0">
          {header}
          {number}
          {showVerdict ? <p className="my-4 max-w-[18ch] text-heading font-normal text-text-primary">{verdict}</p> : null}
          {metaRow(showVerdict ? undefined : 'mt-4')}
        </div>
        {aside ? <div className="flex min-w-0 flex-col gap-4">{aside}</div> : null}
      </Tile>
    )
  }

  return (
    <Tile {...landmark} padding={24} aria-busy={isLoading || undefined} className={clsx('flex flex-col self-start', className)}>
      {header}
      {number}
      {metaRow('mt-4')}
      {visual && !isLoading ? <div className="mt-6 min-w-0">{visual}</div> : null}
    </Tile>
  )
}

export default ScoreTile
